/**
 * Computeds globais sobre store.ts + model/ (FE §6.3, DE-B02). `features/` não importa `router/` (§2.2.2): lê os
 * seletores de rota por aqui, pelos hooks de state/hooks.ts. As regras são as funções puras de model/scene.ts (as
 * mesmas que router/selectors.ts e router/offices.ts reexportam), então não há duas versões de nenhuma regra.
 * `phase` e `headline` (§13.1.1, §13.2; Fase 4) leem o relógio da fonte (`now`, tique de 1 s): o valor só muda quando
 * a fase ou o estado mudam, então quem os assina não renderiza a cada segundo. `wide` (§4.4.1) é do AppShell:
 * `isWide()` de app/layoutMode.ts (`state/` não importa `app/`).
 */
import { computed } from '@preact/signals';
import { headlineAt } from '../model/headline';
import { type NightPhase, nightPhase } from '../model/nightPhase';
import {
  availableOffices,
  effectiveMapMode,
  focusUf as focusUfOf,
  isAbroad as isAbroadOf,
  viewKey as viewKeyOf,
} from '../model/scene';
import type { HeadlineState, MapMode, RouteOffice, UfCode } from '../model/types';
import { commentaryPath, commentaryStatus, config, ctx, layout, now, route, timeline, ui, view } from './store';

/** Fase da noite (§13.1.2): `nightPhase(view, now.tMin, features.governors)`; no replay, sobre a vista exibida. */
export const phase = computed<NightPhase>(() =>
  nightPhase(view.value, now.value.tMin, config.value.features.governors),
);
/**
 * Estado da manchete exibido (§13.2; FE §9.2, G-10): `view.headline` (histerese já aplicada pelo FeedClient), salvo a
 * contagem regressiva e a abertura das 17h00, recalculadas no tique de `now` (H8) sem esperar snapshot novo.
 */
export const headline = computed<HeadlineState>(() =>
  headlineAt(view.value, now.value.tMin, config.value.features.probability),
);

/** `"{office}:{placeKey}"`; a zona não entra (§5.1). */
export const viewKey = computed<string>(() => viewKeyOf(route.value));
/** UF em foco (UF ou município), ou null (§5.1). */
export const focusUf = computed<UfCode | null>(() => focusUfOf(route.value));
/** Exterior, país ou cidade do exterior (§5.1). */
export const isAbroad = computed<boolean>(() => isAbroadOf(route.value));
/** Abas disponíveis, ordem fixa (§5.4.1); com menos de 2, OfficeTabs não é renderizado. */
export const offices = computed<RouteOffice[]>(() => availableOffices(ctx.value));
/** Modo que o mapa desenha (§5.1, §9.4.1); a URL guarda o pedido. */
export const mapMode = computed<MapMode>(() =>
  effectiveMapMode(route.value, timeline.value.live, layout.value.mode, config.value.features),
);
/** `hasFocus` de §4.4.1: lugar diferente de Brasil ou perfil de candidato aberto. */
export const hasFocus = computed<boolean>(() => route.value.place.kind !== 'br' || ui.value.profile !== null);
/** TV ligada. */
export const tvOn = computed<boolean>(() => ui.value.tv.on);

/**
 * O minuto do relógio da fonte (`floor(now.tMin)`): quem só precisa do minuto (o fechamento das urnas do Exterior,
 * §8.5 e §7.12) assina este e não o tique de 1 s de `now` (o cartão inteiro se redesenhava a cada segundo).
 */
export const nowMinute = computed<number>(() => Math.floor(now.value.tMin));

/**
 * A tela de comentários existe agora no ao vivo (ORD-F-026, PR 6): `commentary_timeline` e as mesmas condições do painel
 * de comentários (`commentary`, a camada no snapshot, depois da abertura, fora da TV). Derivado, e não a rota: quem o lê
 * (a casca do App) só é avisado quando ele muda, e não a cada troca de lugar (DECISOES §26).
 */
export const liveScreenAvailable = computed<boolean>(() => {
  const f = config.value.features;
  return (
    f.commentary_timeline &&
    f.commentary &&
    commentaryPath.value !== null &&
    // com o índice `off`, a tela some como sem comentários (pré-mortem de 25/10, P-87)
    commentaryStatus.value !== 'off' &&
    phase.value !== 'pre_open' &&
    layout.value.mode !== 'tv'
  );
});
/** A tela está aberta no ao vivo (a rota pede e ela existe). */
export const liveScreenOn = computed<boolean>(() => liveScreenAvailable.value && route.value.screen === 'comments');
